import { useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useEffect } from 'react';
import { AppState } from 'react-native';

import { THIRTY_SECONDS } from '@/constants/time';
import { createPrimumChatTransport } from '@/services/chat/createPrimumChatTransport';
import { invalidateInboxDebounced } from '@/services/conversations/cache';
import { getInquiryStatsQueryOptions } from '@/services/getInquiryStats';
import { userIdAtom } from '@/services/globalState';
import {
  selectSetChatTransport,
  useChatTransportStore,
} from '@/services/stores/chatTransportStore';
import { shouldFetchUserScoped } from '@/util/authTokens';
import { invalidateUnreadInquiriesDebounced } from '@/util/inboxInvalidation';

/**
 * How often the inbox is re-read while chat runs on the Primum transport.
 *
 * Transitional. Under Twilio, the SDK's client-wide events keep the inbox
 * list, unread badge and stats current. The inbox socket does that for native
 * conversations, but the backend does not yet publish its hints for inquiry
 * messages, so without this the inbox would only move on a resume or a
 * refetch. Delete it once inquiry messages hint the inbox channel.
 */
const INBOX_REFRESH_INTERVAL_MS = THIRTY_SECONDS;

/**
 * The chat session on Primum's own API: one transport per effective viewer,
 * written to the transport store for as long as that viewer is signed in, and
 * no Twilio client, token or socket.
 *
 * Changing the effective viewer (signing out, or starting or stopping acting
 * as someone) builds a new transport, as the Twilio session rebuilds its
 * client. The old one is shut down, which stops any conversation it was still
 * polling, and it leaves the store only if it is still the one there.
 */
export function usePrimumChatSession(): void {
  const userId = useAtomValue(userIdAtom);

  const setTransport = useChatTransportStore(selectSetChatTransport);

  const queryClient = useQueryClient();

  const canFetch = shouldFetchUserScoped(userId);

  useEffect(() => {
    if (!canFetch || !userId) {
      return undefined;
    }

    const transport = createPrimumChatTransport(userId);

    setTransport(transport);

    return () => {
      setTransport((current) => (current === transport ? undefined : current));

      transport.shutdown().catch(() => undefined);
    };
  }, [canFetch, userId, setTransport]);

  useEffect(() => {
    if (!canFetch) {
      return undefined;
    }

    const timer = setInterval(() => {
      if (AppState.currentState !== 'active') {
        return;
      }

      invalidateInboxDebounced(queryClient, 0);

      invalidateUnreadInquiriesDebounced(queryClient, 0);

      queryClient
        .invalidateQueries({
          queryKey: getInquiryStatsQueryOptions(userId).queryKey,
          refetchType: 'active',
        })
        .catch(() => undefined);
    }, INBOX_REFRESH_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [canFetch, queryClient, userId]);
}
